import { ArrowRight, List, Phone, Globe, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Building2 } from "lucide-react";
import { BadgeCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate, Link } from "@tanstack/react-router";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export function CTACatalogo({ slug }: { slug: string }) {
  return (
    <div className="bg-[#0C1A12] rounded-[20px] p-10 mt-14">
      <div className="inline-block px-3 py-1 bg-[#C6F764]/10 border border-[#C6F764]/20 rounded-full mb-6">
        <span className="text-[#C6F764] text-xs font-bold uppercase tracking-wider">Gopage</span>
      </div>
      <h3 className="text-white text-[26px] font-bold mb-4 leading-tight">
        Monte seu catálogo digital e venda o que você trouxe
      </h3>
      <p className="text-[#D1D5DB] text-[17px] mb-6">
        Crie um catálogo com link único, mande suas clientes pelo WhatsApp e receba o pedido pronto, sem anotar nada em caderno.
      </p>
      <div className="text-[#C6F764] font-semibold text-[17px] mb-8">
        A partir de R$ 39,90/mês
      </div>
      <a 
        href={`https://gopage.bio?utm_source=listafornecedores&utm_medium=artigo&utm_campaign=trial&utm_content=${slug}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        <Button variant="onDark" className="w-full md:w-auto h-[56px] px-8 font-semibold text-[17px] rounded-[12px] flex items-center justify-center gap-2 transition-all">
          Testar grátis por 7 dias
          <ArrowRight className="w-5 h-5" />
        </Button>
      </a>
    </div>
  );
}

export function CTALista({ status: propStatus }: { status?: string | null | undefined }) {
  const { profile, user, authState } = useAuth();
  const loading = authState === 'carregando';
  const navigate = useNavigate();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  
  // Se o status for passado via prop, usa ele, senão usa do contexto
  const currentStatus = propStatus || profile?.status;
  const isAprovado = currentStatus === 'aprovado';

  const { data: previewFornecedores = [], isLoading: isLoadingPreview } = useQuery({
    queryKey: ['preview-fornecedores-cta', isAprovado],
    queryFn: async () => {
      if (isAprovado) {
        const { data } = await supabase
          .from('fornecedores')
          .select('id, nome, cidade, uf, polo, verificado, destaque, logo_url, telefone, site, instagram, email, segmentos(nome, slug, icone)')
          .eq('status', 'aprovado')
          .filter('suspeito', 'is', false)
          .order('destaque', { ascending: false })
          .order('verificado', { ascending: false })
          .limit(3);
        
        return (data || []).map(f => ({
          ...f,
          segmento_nome: (f.segmentos as any)?.nome,
          segmento_slug: (f.segmentos as any)?.slug,
          segmento_icone: (f.segmentos as any)?.icone
        }));
      } else {
        const { data } = await supabase.rpc('preview_fornecedores');
        return (data || []).slice(0, 3);
      }
    },
    enabled: !loading,
    staleTime: 1000 * 60 * 15,
  });
  
  return (
    <div className={cn(
      "rounded-[20px] p-8 mt-14 border-2 border-[#C6F764]",
      isAprovado ? "bg-white shadow-sm" : "bg-[#F7F9F5]"
    )}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
        <div>
          <div className="w-12 h-12 bg-[#C6F764] rounded-xl flex items-center justify-center mb-6">
            <List className="w-6 h-6 text-[#0C1A12]" />
          </div>
          <h3 className="text-[#0C1A12] text-[24px] font-bold mb-3 leading-tight">
            {isAprovado ? "Fornecedores recomendados para você" : "Procurando mais fornecedores?"}
          </h3>
          <p className="text-[#4B5563] text-[17px] max-w-xl">
            {isAprovado 
              ? "Confira alguns dos fornecedores disponíveis agora no diretório completo para sua loja." 
              : "A Lista de Fornecedores reúne fornecedores e atacadistas brasileiros com CNPJ conferido, organizados por segmento e cidade."}
          </p>
        </div>
        
        <Button 
          onClick={() => {
            if (isAprovado) {
              navigate({ to: '/fornecedores', search: { page: 1 } });
            } else if (!user) {
              setLoginModalOpen(true);
            } else {
              navigate({ to: profile?.status === 'pendente' ? '/aguardando' : '/acesso-negado' });
            }
          }}
          variant="onLight" 
          className="rounded-[12px] px-8 h-[56px] bg-[#0C1A12] text-white hover:bg-[#0C1A12]/90 font-bold text-[16px] whitespace-nowrap"
        >
          {isAprovado ? "Ver todos os fornecedores" : "Ver a lista completa"}
        </Button>
      </div>

      {!isLoadingPreview && previewFornecedores.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {previewFornecedores.map((f: any) => (
            <Card 
              key={f.id} 
              className="group cursor-pointer border-[#E5E7EB] rounded-[16px] hover:shadow-md transition-all duration-300 overflow-hidden bg-white"
              onClick={() => {
                if (loading) return;
                if (!isAprovado) {
                  setLoginModalOpen(true);
                } else {
                  navigate({ to: `/fornecedores/${f.id}` as any });
                }
              }}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-7 h-7 bg-[#C6F764] rounded-lg flex items-center justify-center shrink-0">
                    <Building2 className="w-[14px] h-[14px] text-[#0C1A12]" />
                  </div>
                  {f.verificado && (
                    <div className="flex items-center gap-1 px-2 py-0.5 bg-[#C6F764] rounded-full text-[9px] font-bold text-[#0C1A12] uppercase tracking-wider">
                      <BadgeCheck className="w-2.5 h-2.5" />
                      Verificado
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <h4 className="text-[15px] font-bold text-[#1A1A1A] line-clamp-1 group-hover:text-[#0C1A12]">
                    {f.nome}
                  </h4>
                  <span className="text-xs text-[#6B7280]">
                    {f.segmento_nome}
                  </span>
                </div>

                {isAprovado ? (
                  <div className="flex gap-2">
                    {f.telefone && (
                      <div className="flex-1 flex items-center justify-center gap-1.5 bg-[#25D366]/10 text-[#25D366] rounded-[8px] py-2 text-[11px] font-bold">
                        <Phone className="w-3 h-3" /> WhatsApp
                      </div>
                    )}
                    {f.site && (
                      <div className="flex-1 flex items-center justify-center gap-1.5 bg-gray-50 text-[#0C1A12] rounded-[8px] py-2 text-[11px] font-bold">
                        <Globe className="w-3 h-3" /> Site
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-[#F7F9F5] rounded-[8px] p-2 relative overflow-hidden">
                    <div className="space-y-1.5 blur-[4px] select-none opacity-40">
                      <div className="h-2 bg-gray-300 rounded-full w-3/4"></div>
                      <div className="h-2 bg-gray-300 rounded-full w-1/2"></div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Lock className="w-4 h-4 text-[#6B7280]" />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!isAprovado && !user && (
        <div className="text-center pt-2 border-t border-[#E5E7EB]">
          <a href="/entrar" className="text-[#4B5563] font-medium hover:text-[#0C1A12] text-[15px]">
            Já é cliente Gopage? <span className="underline">Entrar na conta</span>
          </a>
        </div>
      )}

      {/* Modal de Acesso Restrito (Sincronizado com a Home) */}
      <Dialog open={loginModalOpen} onOpenChange={setLoginModalOpen}>
        <DialogContent className="sm:max-w-md rounded-[32px] p-8 border-none bg-white">
          <DialogHeader className="flex flex-col items-center text-center space-y-4">
            <div className="w-20 h-20 bg-[#C6F764] rounded-[24px] flex items-center justify-center mb-2 animate-in zoom-in duration-300">
              <Lock className="w-10 h-10 text-[#0C1A12]" />
            </div>
            <DialogTitle className="text-3xl font-black text-[#0C1A12] leading-tight">
              Acesso exclusivo para clientes Gopage
            </DialogTitle>
            <DialogDescription className="text-lg text-[#6B7280] font-medium">
              Esta é uma lista privada. Para ver os contatos dos fornecedores, você precisa ter uma conta Gopage ativa.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3 mt-6">
            <Button 
              asChild 
              className="h-[60px] rounded-2xl bg-[#0C1A12] text-white hover:bg-[#0C1A12]/90 text-lg font-bold"
            >
              <Link to="/entrar">Já sou cliente (Entrar)</Link>
            </Button>
            <Button 
              asChild 
              variant="outlineDark"
              className="h-[60px] rounded-2xl text-lg font-bold border-2"
            >
              <Link to="/cadastro">Criar meu acesso</Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function cn(...args: (string | undefined | null | false)[]) {
  return args.filter(Boolean).join(" ");
}
